import React, {useState} from 'react'
import gql from 'graphql-tag'
import {DateUtils} from 'react-day-picker'
import moment from 'lib/localizedMoment'
import Query from 'components/shared/Query'
import Button from 'components/shared/Button'
import WeekNumberPicker from 'shared/components/WeekNumberPicker'
import {momentRange} from 'shared/lib/dateHelpers'
import {getUnavailabilityReason} from '../helpers'
import TimeSlotPicker from '../TimeSlotPicker'
import './index.scss'

const resourceAvailabilityQuery = gql`
  query ResourceAvailabilityQuery(
    $webOrderId: ID!,
    $serviceProviderIds: [ID]!
    $dates: [Date]!,
  ) {
    webOrder(id: $webOrderId) {
      id
      serviceProviderAvailability(dates: $dates, serviceProviderIds: $serviceProviderIds) {
        date
        isAvailable
        unavailabilityReason
        serviceProvider {
          ...ServiceProviderProperties
        }
        pendingBookings {
          ...PendingBookingProperties
        }
      }
    }
  }

  fragment PendingBookingProperties on PendingBooking {
    id
    startsAt
    endsAt
    bookingFee
    serviceProvider {
      ...ServiceProviderProperties
    }
  }

  fragment ServiceProviderProperties on ServiceProvider {
    id
    name
  }
`

function getLookupVariables({ week, year }) {
  const selectedWeek = moment().year(year).isoWeek(week)

  return {
    from: selectedWeek.clone().startOf('week'),
    to: selectedWeek.clone().endOf('week'),
  }
}

const ResourceAvailabilityForDates = ({webOrder, serviceProvider, reservedBooking, selectedBooking, dates, onSelect, onRemove}) => {
  return (
    <Query
      inlineLoading
      inlineError
      notifyOnNetworkStatusChange
      fetchPolicy="network-only"
      query={resourceAvailabilityQuery}
      variables={{
        webOrderId: webOrder.id,
        dates: dates.map(d => moment(d).format('YYYY-MM-DD')),
        serviceProviderIds: [serviceProvider.id],
      }}
    >
      {({getData}) => {
        const availabilities = getData('webOrder.serviceProviderAvailability', [])
        return (
          <div className="resource-availability-for-dates">
            {dates.map((date, i) => {
              const availabilityOnDate = availabilities.find(a => a.date === date.format('YYYY-MM-DD'))

              const isAvailable = availabilityOnDate.isAvailable
              const unavailabilityReason = !isAvailable && availabilityOnDate.unavailabilityReason

              const isBeingSelected = selectedBooking
                && availabilityOnDate.pendingBookings
                && availabilityOnDate.pendingBookings.find(pb => pb.id === selectedBooking.id)

              const isSelected = reservedBooking
                && availabilityOnDate.pendingBookings
                && availabilityOnDate.pendingBookings.find(pb => pb.id === reservedBooking.id)

              const isInPast = !DateUtils.isFutureDay(date.toDate())

              const classNames = ['day-container', (!isAvailable || isInPast) ? 'unavailable' : 'available']
              if (isSelected) classNames.push('selected')

              return (
                <div key={i} className={classNames.join(' ').trim()}>
                  <span className="weekday">{date.format('dddd')}</span>
                  <span className="day">{date.format('DD. MMMM')}</span>
                  {unavailabilityReason ? (
                    <span className="unavailable-reason">
                      {getUnavailabilityReason(unavailabilityReason)}
                    </span>
                  ) : (
                    <>
                      {isSelected ? (
                        <>
                          <span>{moment(reservedBooking.startsAt).format('HH:mm')}</span>
                          <Button
                            type="primary"
                            danger
                            block
                            onClick={onRemove}
                            loading={isBeingSelected}
                          >
                            Fjern
                          </Button>
                        </>
                      ) : (
                        <TimeSlotPicker
                          pendingBookings={availabilityOnDate.pendingBookings}
                          onSelect={onSelect}
                          isBeingSelected={isBeingSelected}
                          disabled={isBeingSelected || isInPast}
                        />
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )
      }}
    </Query>
  )
}

const WeeklyAvailability = ({webOrder, serviceProvider, reservedBooking, selectedBooking, onSelect, onRemove}) => {
  const now = moment()
  const [{week, year}, setOptions] = useState({
    week: now.isoWeek(),
    year: now.year(),
  })

  const {from, to} = getLookupVariables({week, year})
  const range = momentRange(from, to)

  return (
    <div className="weekly-availability">
      <WeekNumberPicker
        week={week}
        year={year}
        onChange={(week, year) => setOptions({week, year})}
      />
      <ResourceAvailabilityForDates
        webOrder={webOrder}
        serviceProvider={serviceProvider}
        dates={range}
        reservedBooking={reservedBooking}
        selectedBooking={selectedBooking}
        onSelect={onSelect}
        onRemove={onRemove}
      />
    </div>
  )
}

export default WeeklyAvailability
